/**
 * @deprecated This module is deprecated in favor of DataSource transformers.
 * 
 * Migration Guide:
 * ----------------
 * Instead of transforming CardData to TableData, use DataSource transformers directly:
 * 
 * Old approach (deprecated):
 * ```typescript
 * import { transformCardsToTableData } from "@/lib/utils/cardToTableTransformer";
 * const tableData = transformCardsToTableData(cards, itemType, ...);
 * ```
 * 
 * New approach (recommended):
 * ```typescript
 * import { useDataSource } from "@/hooks/useDataSource";
 * const { tableData, fieldMetadata } = useDataSource('objects');
 * // tableData.items contains TableDataRow[] directly
 * ```
 * 
 * Or use the DataSource directly:
 * ```typescript
 * import { objectsDataSource } from "@/lib/dataSources/sources/objects";
 * const tableOutput = objectsDataSource.transformers.toTableItems(rawData, context);
 * ```
 * 
 * This file will be removed after all item types (blueprints, documents, dialogs)
 * have been migrated to the DataSource pattern.
 * 
 * See: platform/src/lib/dataSources/sources/objects.ts
 * See: platform/src/hooks/useDataSource.ts
 */

import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";
import { TableItem, TableDataRow } from "@/components/table-main/main_table_parts/MT_Datatable";
import { DataViewMode, DataViewOption } from "@/components/cards/widgets/TableFilter";
import { getFolderPath } from "@/lib/stores/folderStore";

/**
 * Column configuration for each item type
 * Note: Type, Tags, and Identifiers are excluded from library items as they are not standard metadata columns
 */
const COLUMN_CONFIGS: Record<string, string[]> = {
  documents: ["Title", "Description", "Owner", "Tags", "Status", "Last Update"],
  blueprints: ["Name", "Description", "Owner", "Tags", "Last Update"],
  dialogs: ["Name", "Dialog Key", "Description", "Tags", "Status", "Last Update"],
  library: ["Name", "Owner", "Status", "Last Update", "Folder", "Blueprint", "Blueprint Version", "Version"],
  variables: ["Name", "Type", "Group", "Values", "Folder", "Description", "Last Update"],
};

/**
 * Transform a single card into a table row
 */
function transformCardToRow(
  card: CardData, 
  itemType: string,
  folderBadge?: string,
  depth: number = 0,
  currentUserId?: string,
  blueprintsMap?: Map<string, string>,
  folderPathLookup?: (folderId: string | null | undefined) => string
): TableDataRow {
  const columns = COLUMN_CONFIGS[itemType] || COLUMN_CONFIGS.documents;
  
  // Determine if the row is locked:
  // - Item must have Locked edit_permissions
  // - Current user must NOT be the owner
  const ownerId = card.ownerId as string | undefined;
  const editPermissions = card.editPermissions as 'Open' | 'Locked' | undefined;
  const isLocked = editPermissions === 'Locked' && 
                   ownerId !== undefined && 
                   currentUserId !== undefined && 
                   ownerId !== currentUserId;

  // Debug logging for lock status
  if (editPermissions === 'Locked') {
    console.log('[Lock Debug]', {
      cardTitle: card.title,
      ownerId,
      currentUserId,
      editPermissions,
      isLocked,
      ownerMatch: ownerId === currentUserId,
    });
  }

  return {
    type: "row",
    key: card.id,
    depth,
    folderBadge,
    isLocked,
    ownerId,
    editPermissions,
    content: columns.map((columnKey, index) => {
      const normalizedKey = columnKey.toLowerCase().replace(/\s+/g, "");
      let content: string | boolean | number = "";

      // Map column to card data
      switch (normalizedKey) {
        case "title":
        case "name":
          // Badge is now handled in MT_Datatable, just pass the content
          content = card.title || "";
          break;
        case "description":
          content = card.description || "";
          break;
        case "owner":
          content = card.owner || "no owner";
          break;
        case "folder":
          // Use provided lookup function or fall back to getFolderPath from store
          if (folderPathLookup) {
            content = folderPathLookup(card.folder as string) || "";
          } else {
            content = getFolderPath(card.gameId as string, itemType, card.folder as string) || "";
          }
          break;
        case "identifiers":
          content = Array.isArray(card.identifiers) ? card.identifiers.join(", ") : "";
          break;
        case "blueprint": { 
          const blueprintId = card.blueprint_id as string;
          content = blueprintId && blueprintsMap?.get(blueprintId) || blueprintId || "";
          break;
        }
        case "blueprintversion":
          content = (card.blueprintVersion as string) || "";
          break;
        case "version":
          content = card.version || "";
          break;
        case "tags":
          content = Array.isArray(card.tags) ? card.tags.join(", ") : "";
          break;
        case "status":
          content = card.status || "";
          break;
        case "lastupdate":
          content = card.lastUpdate || "";
          break;
        case "type":
          // For variables, use elementTypeLabel; for library items, use type
          if (itemType === "variables") {
            content = (card.elementTypeLabel as string) || "";
          } else {
            content = (card.type as string) || "";
          }
          break;
        case "dialogkey":
          // For dialogs, try to get dialog_key from card data
          content = (card.dialog_key as string) || "";
          break;
        case "group":
          // For variables, get group name
          content = (card.groupName as string) || "";
          break;
        case "values":
          // For variables, get element count
          const elementCount = card.elementCount as number || 0;
          content = `${elementCount} ${elementCount === 1 ? 'value' : 'values'}`;
          break;
        default:
          content = "";
      }

      return {
        key: columnKey,
        fixedX: index === 0, // First column is fixed
        content,
      };
    }),
  };
}

/**
 * Transform CardData array into table data format for MainTableWidget
 * @param currentUserId - Current user's ID for determining locked status
 * @param blueprintsMap - Optional map of blueprint IDs to names for displaying blueprint names
 * @param folderPathLookup - Optional function to resolve folder ID to display path
 * @deprecated Use objectsDataSource.transformers.toTableItems() or useDataSource hook instead
 */
export function transformCardsToTableData(
  cards: CardData[],
  itemType: string,
  folderBadge?: string,
  depth?: number,
  currentUserId?: string,
  blueprintsMap?: Map<string, string>,
  folderPathLookup?: (folderId: string | null | undefined) => string
): TableItem[] {
  return cards.map((card) => transformCardToRow(card, itemType, folderBadge, depth, currentUserId, blueprintsMap, folderPathLookup));
}

/**
 * Get a field value from raw item data
 */
function getFieldValue(rawItem: any, fieldId: string): string {
  if (!rawItem?.data || !Array.isArray(rawItem.data)) return "";
  
  for (const section of rawItem.data) {
    if (!section.fields || !Array.isArray(section.fields)) continue;
    
    const field = section.fields.find((f: any) => f.id === fieldId);
    if (field) {
      const value = field.value;
      if (value === null || value === undefined) return "";
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") {
        // Handle url type {url, text}
        if (value.text) return value.text;
        if (value.url) return value.url;
        return JSON.stringify(value);
      }
      return String(value);
    }
  }
  return "";
}

/**
 * Get section field values aggregated for display
 */
function getSectionValues(rawItem: any, sectionId: string): string {
  if (!rawItem?.data || !Array.isArray(rawItem.data)) return "";
  
  const section = rawItem.data.find((s: any) => s.id === sectionId);
  if (!section || !section.fields || !Array.isArray(section.fields)) return "";
  
  // Aggregate field values for this section
  const values = section.fields
    .filter((f: any) => f.value !== null && f.value !== undefined && f.value !== "")
    .map((f: any) => {
      const value = f.value;
      if (Array.isArray(value)) return value.join(", ");
      if (typeof value === "object") {
        if (value.text) return value.text;
        if (value.url) return value.url;
        return "";
      }
      return String(value);
    })
    .filter(Boolean);
  
  return values.join(" | ");
}

/**
 * Transform cards to table data based on sections/fields view mode
 * @param currentUserId - Current user's ID for determining locked status
 */
export function transformCardsToSectionFieldTableData(
  cards: CardData[],
  rawItems: any[],
  dataViewMode: DataViewMode,
  selectedSections: string[],
  selectedFields: string[],
  availableSections: DataViewOption[],
  availableFields: DataViewOption[],
  currentUserId?: string
): TableItem[] {
  if (cards.length === 0) return [];
  
  // Create a map of raw items by id for quick lookup
  const rawItemsMap = new Map(rawItems.map(item => [item.id, item]));
  
  // Build column headers based on view mode
  const columnHeaders: string[] = ["Name"]; // Always include name as first column
  
  if (dataViewMode === "sections") {
    // Add selected sections as columns
    selectedSections.forEach(sectionId => {
      const section = availableSections.find(s => s.id === sectionId);
      if (section) {
        columnHeaders.push(section.label);
      }
    });
  } else if (dataViewMode === "fields") {
    // Add selected fields as columns
    selectedFields.forEach(fieldId => {
      const field = availableFields.find(f => f.id === fieldId);
      if (field) {
        columnHeaders.push(field.label);
      }
    });
  }
  
  // Transform each card to a row
  return cards.map((card): TableDataRow => {
    const rawItem = rawItemsMap.get(card.id);
    
    // Determine if the row is locked
    const ownerId = card.ownerId as string | undefined;
    const editPermissions = card.editPermissions as 'Open' | 'Locked' | undefined;
    const isLocked = editPermissions === 'Locked' && 
                     ownerId !== undefined && 
                     currentUserId !== undefined && 
                     ownerId !== currentUserId;
    
    return {
      type: "row",
      key: card.id,
      depth: 0,
      isLocked,
      ownerId,
      editPermissions,
      content: columnHeaders.map((header, index) => {
        let content: string | boolean | number = "";
        
        if (index === 0) {
          // First column is always the name
          content = card.title || "";
        } else if (dataViewMode === "sections") {
          // Get section value
          const sectionId = selectedSections[index - 1];
          content = getSectionValues(rawItem, sectionId);
        } else if (dataViewMode === "fields") {
          // Get field value
          const fieldId = selectedFields[index - 1];
          content = getFieldValue(rawItem, fieldId);
        }
        
        return {
          key: header,
          fixedX: index === 0, // First column is fixed
          content,
        };
      }),
    };
  });
}

